import { useRef, useState } from 'react';
import { QuotePostResponse } from '@lio/api-types';
import { useCreateQuote } from '@services/quotes';
import {
  NonLiveJurisdictionAction,
  NonLiveJurisdictionDialogCancelledError,
  QuotePostRequestBodyWithNonLiveJurisdictionAction,
  isNonLiveJurisdictionError,
} from './nonLiveJurisdiction';

type PendingResolvers = {
  resolve: (value: QuotePostResponse) => void;
  reject: (reason?: unknown) => void;
};

/**
 * Wraps quote creation so that, for internal users only, a "jurisdiction not live" rejection
 * (see nonLiveJurisdiction.ts for how that's currently detected) surfaces a confirmation dialog
 * with two explicit choices instead of a generic error. Whichever choice the user makes, the
 * same request is resubmitted with the corresponding `nonLiveJurisdictionAction` value, and the
 * promise returned by the original `createQuote` call resolves/rejects with that outcome.
 */
export const useCreateQuoteWithNonLiveJurisdictionCheck = (isInternalUser: boolean) => {
  const { createQuoteAsync } = useCreateQuote();
  const [pendingQuoteBody, setPendingQuoteBody] = useState<QuotePostRequestBodyWithNonLiveJurisdictionAction | null>(
    null,
  );
  const [isResubmitting, setIsResubmitting] = useState(false);
  const pendingResolvers = useRef<PendingResolvers | null>(null);

  const createQuote = (quoteBody: QuotePostRequestBodyWithNonLiveJurisdictionAction): Promise<QuotePostResponse> =>
    createQuoteAsync(quoteBody).catch(
      (error) =>
        new Promise<QuotePostResponse>((resolve, reject) => {
          isNonLiveJurisdictionError(error).then((isJurisdictionError) => {
            if (isInternalUser && isJurisdictionError) {
              pendingResolvers.current = { resolve, reject };
              setPendingQuoteBody(quoteBody);
            } else {
              reject(error);
            }
          });
        }),
    );

  const resolveWithAction = async (action: NonLiveJurisdictionAction) => {
    if (!pendingQuoteBody) return;
    setIsResubmitting(true);
    const resolvers = pendingResolvers.current;
    try {
      const response = await createQuoteAsync({ ...pendingQuoteBody, nonLiveJurisdictionAction: action });
      resolvers?.resolve(response);
    } catch (error) {
      resolvers?.reject(error);
    } finally {
      setIsResubmitting(false);
      setPendingQuoteBody(null);
      pendingResolvers.current = null;
    }
  };

  const cancelNonLiveJurisdictionDialog = () => {
    pendingResolvers.current?.reject(
      new NonLiveJurisdictionDialogCancelledError('Quote creation cancelled: jurisdiction not live.'),
    );
    pendingResolvers.current = null;
    setPendingQuoteBody(null);
    setIsResubmitting(false);
  };

  return {
    createQuote,
    isNonLiveJurisdictionDialogOpen: !!pendingQuoteBody,
    isResubmitting,
    onCreateAndDecline: () => resolveWithAction('declineIfInvalid'),
    onCreateInternalOnly: () => resolveWithAction('createInternalOnly'),
    onCancelNonLiveJurisdictionDialog: cancelNonLiveJurisdictionDialog,
  };
};
